import { StrictMode } from 'react'
import { createRoot, hydrateRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router'
import {
  HydrationBoundary,
  QueryClient,
  QueryClientProvider,
  type DehydratedState,
} from '@tanstack/react-query'

import { App } from './App'
import './i18n'
import './styles.css'

declare global {
  interface Window {
    /** Query cache serialised into the page by the prerender script. */
    __TIART_STATE__?: DehydratedState
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Gallery content changes when Iryna publishes something, which also
      // triggers a full rebuild — so cached data staying warm for a few
      // minutes costs nothing and saves a great many requests.
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

const dehydratedState: DehydratedState = window.__TIART_STATE__ ?? { mutations: [], queries: [] }

const tree = (
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <HydrationBoundary state={dehydratedState}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </HydrationBoundary>
    </QueryClientProvider>
  </StrictMode>
)

const container = document.getElementById('root')
if (!container) {
  throw new Error('#root is missing from index.html')
}

// A prerendered page has markup to attach to; the dev server and any page that
// was not prerendered (the admin panel, or a URL published since the last
// build) start from an empty container.
//
// The test is firstElementChild, not hasChildNodes: the raw template contains
// the `<!--app-html-->` comment, and a comment is a child node. Checking for
// any child would send those pages down the hydration path with nothing to
// hydrate against, which React reports as a mismatch and recovers from by
// re-rendering everything — a wasted pass and a console full of errors.
if (container.firstElementChild) {
  hydrateRoot(container, tree)
} else {
  createRoot(container).render(tree)
}
